import type {ReadingSelection} from "../../types";
import type {DibDocument} from "./types";

export const MAX_SELECTION_CHARS = 5000;

// Document augmentations are navigation/UI, not part of the quoted passage.
const EXCLUDED_CONTENT = '[data-reading-context-ignore], button, input, select, textarea, [hidden], [aria-hidden="true"], script, style';
const BLOCK_ELEMENTS = new Set(["P", "DIV", "SECTION", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "UL", "OL", "TABLE", "TR", "BLOCKQUOTE", "PRE"]);

function selectedText(range: Range): string {
    const ancestor = range.commonAncestorContainer;
    const element = ancestor instanceof Element ? ancestor : ancestor.parentElement;
    if (element?.closest(EXCLUDED_CONTENT)) return "";
    const fragment = range.cloneContents();
    fragment.querySelectorAll(EXCLUDED_CONTENT).forEach(element => element.remove());
    function text(node: Node): string {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
        const tag = node instanceof Element ? node.tagName : "";
        if (tag === "BR") return "\n";
        const content = Array.from(node.childNodes, text).join("");
        if (BLOCK_ELEMENTS.has(tag)) return `\n${content}\n`;
        if (tag === "TD" || tag === "TH") return `${content}\t`;
        return content;
    }
    return text(fragment).replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

export function readArticleSelection(article: HTMLElement, selection: Selection | null, doc: DibDocument, segmentId?: string): ReadingSelection | null {
    if (!selection || selection.isCollapsed || selection.rangeCount !== 1) return null;
    const range = selection.getRangeAt(0);
    if (!article.contains(range.startContainer) || !article.contains(range.endContainer)) return null;
    const text = selectedText(range);
    if (!text) return null;
    const toc = new Map(Object.values(doc.toc).map(node => [node.attributes.id, node]));
    const sections = new Map<string, string | undefined>();
    // Visit selected text, not intersecting section containers: a parent section
    // can intersect the range even when all selected text belongs to its child.
    const walker = document.createTreeWalker(article, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (!range.intersectsNode(node) || node.parentElement?.closest(EXCLUDED_CONTENT)) continue;
        const start = node === range.startContainer ? range.startOffset : 0;
        const end = node === range.endContainer ? range.endOffset : node.textContent?.length;
        if (!node.textContent?.slice(start, end).trim()) continue;
        let element = node.parentElement;
        while (element && element !== article) {
            const section = toc.get(element.id);
            if (section) {
                sections.set(section.attributes.id, section.attributes.title);
                break;
            }
            element = element.parentElement;
        }
    }
    const sectionIds = [...sections.keys()];
    const actualSegmentId = doc.segmentId || segmentId || undefined;
    return {
        text: text.slice(0, MAX_SELECTION_CHARS),
        segmentId: actualSegmentId,
        segmentTitle: actualSegmentId ? toc.get(actualSegmentId)?.attributes.title : undefined,
        sectionIds,
        // Keep the first section label for the quote chip and older messages.
        sectionId: sectionIds[0],
        sectionTitle: sections.get(sectionIds[0]),
        truncated: text.length > MAX_SELECTION_CHARS,
    };
}
